import { HeartPulse } from 'lucide-react';
import type { VerticalContent } from '../VerticalLandingPage';

export const healthcareContent: VerticalContent = {
  vertical: 'healthcare',
  industryLabel: 'Healthcare',
  heroIcon: HeartPulse,
  heroBadge: 'For health system ops and clinic admin',
  heroHeadline: 'Coordinate clinical projects across sites — without losing the audit trail.',
  heroSub:
    'Karyva runs accreditation prep, equipment commissioning, multi-site rollouts, and compliance projects the way regulated teams need to: with strict access boundaries, immutable evidence trails, and per-role data visibility that survives any audit.',
  trustStats: [
    { value: 'Strict', label: 'ACL mode for PHI-adjacent data' },
    { value: 'Evidence', label: 'on every status transition' },
    { value: '14-day', label: 'free trial, no card needed' },
  ],
  pains: [
    {
      title: 'Accreditation prep lives in a 200-tab spreadsheet',
      body:
        'Joint Commission, CMS, state surveyors — every survey has its own document checklist, and every checklist lives in a different binder or shared drive. The administrator hired to keep it together can\'t take vacation.',
    },
    {
      title: 'Multi-site rollouts coordinate like herding cats',
      body:
        'A new EHR module, a Press Ganey workflow, a system-wide policy update — each site adapts differently, status gets reported inconsistently, and the system office can\'t tell which sites are actually live.',
    },
    {
      title: 'PHI-adjacent project data leaks across teams',
      body:
        'Quality improvement projects reference patient cohorts. Equipment commissioning lists serial numbers. Compliance projects name specific incidents. Generic PM tools let every user see every project — that\'s a HIPAA finding waiting to happen.',
    },
    {
      title: 'Audit asks "show me when this was approved" and nobody can',
      body:
        'When the surveyor asks who approved the revised protocol on March 14th, the team digs through Outlook for an hour, can\'t find the email, and ends up writing an exception. That exception becomes a citation.',
    },
  ],
  bridges: [
    {
      painHook: 'Accreditation spreadsheets nobody owns',
      feature: 'Survey-ready checklist templates with evidence on each item',
      body:
        'Pre-built templates for Joint Commission, CMS COPs, and major state survey programs. Each checklist item carries its own evidence slot — policy PDF, signed attestation, training completion — and exports to a survey-ready binder in one click.',
    },
    {
      painHook: 'Multi-site rollout chaos',
      feature: 'Per-site project clones with system-office rollup view',
      body:
        'Define the rollout once, clone to each site, see consolidated status without losing per-site nuance. Site admins manage their work; the system office sees the portfolio without micromanaging the local project.',
    },
    {
      painHook: 'PHI-adjacent data visible to wrong roles',
      feature: 'Strict resource-scoped access control (ACL-required mode)',
      body:
        'Flip your org to ACL-required mode and users only see projects they\'ve been explicitly granted access to. Field-level visibility hides cohort sizes, dollar amounts, or incident details from junior coordinators. Built for organizations where the answer to "who can see this?" must be defensible.',
    },
    {
      painHook: 'Audit trails that can\'t prove what happened',
      feature: 'Immutable, exportable activity log on every change',
      body:
        'Every status change, document upload, approval, and field edit is logged with user, timestamp, and prior value. Export the trail as a tamper-evident PDF for the surveyor. We built this to FedRAMP-High constraints; your survey is the easy case.',
    },
  ],
  workflowTitle: 'A multi-site accreditation cycle, in Karyva',
  workflowSteps: [
    {
      when: 'Survey window opens',
      what:
        'System-office admin spins up a Joint Commission checklist template per site. Each site\'s quality lead is granted access to only their checklist. The system office sees aggregated readiness; sites see only their own work.',
    },
    {
      when: '90 days before survey',
      what:
        'Karyva fires reminders for any checklist item still missing evidence. The system office gets a dashboard of red/yellow/green by site without anyone writing a status email.',
    },
    {
      when: 'Day of survey',
      what:
        'Surveyor asks for documentation on a specific finding. Quality lead opens Karyva, pulls the signed protocol with the timestamp and approval chain, prints the audit trail. Done in under two minutes.',
    },
    {
      when: 'Post-survey',
      what:
        'Citations become corrective-action projects with deadlines, owners, and evidence requirements. Track every CAP through resolution; the next survey starts from a cleaner baseline.',
    },
  ],
  whyKaryva: [
    'Built for regulated work: immutable audit trail, encrypted PII fields, and FedRAMP-High-aligned controls.',
    'Strict ACL mode gives you defensible answers to "who could see this?" — required by HIPAA, expected by surveyors.',
    'Per-site project clones with portfolio rollup so the system office can govern without micromanaging.',
    'Survey-ready templates for Joint Commission, CMS, and major state programs — not a blank board.',
  ],
  testimonial: {
    quote:
      'For the first time, our last survey ended without a single "find me that document" pause. Everything was where it should be.',
    name: 'Director of Quality (pilot customer)',
    role: 'Regional health system — 6 hospitals, 40 ambulatory sites',
  },
  logos: ['Northland Health', 'Riverway Medical', 'Summit Care', 'Coastal Health Partners'],
  faqs: [
    {
      q: 'Is Karyva HIPAA-compliant? Do you sign a BAA?',
      a: 'Karyva is built to meet HIPAA technical safeguards (encryption at rest and in transit, audit logging, access controls, automatic logoff). We sign BAAs with healthcare customers as part of the contract. Note that Karyva is project-management software — we recommend not storing PHI in project fields, and our compliance templates are designed around that boundary.',
    },
    {
      q: 'Can we restrict project visibility by site or department?',
      a: 'Yes — this is what we built strict ACL mode for. Each user only sees projects they\'ve been granted explicit access to (directly or via a portfolio/program). System-office users see everything; site users see their site; vendors see the one project they\'re on.',
    },
    {
      q: 'Do you integrate with our EHR or quality system?',
      a: 'Karyva is intentionally not an EHR. We integrate via standard APIs and webhooks with systems like Epic, Cerner, and quality platforms — bringing project-level visibility into them, not replacing them. Most integrations are read-only on the EHR side by design.',
    },
    {
      q: 'How long does implementation take for a health system?',
      a: 'A single site can be running survey-ready templates in under two weeks. A multi-site health system typically takes 4-6 weeks to roll out, including ACL setup, site-by-site onboarding, and dashboard configuration. No six-month consulting engagement.',
    },
  ],
};
